/**
 * Authentication Vuex Module
 * Handles authentication state and actions using the API
 */

import { authApi } from '@/services/api';
import { tokenManager } from '@/utils/token-manager';

export const state = {
  accessToken: tokenManager.getAccessToken(),
  currentUser: null,
  // Ma trận quyền field tiền ({price, order_item_price, payment, cod, freeship}) từ
  // GET /auth/me/field-permissions — nguồn xác thực CHÍNH THỨC cho
  // `helpers/orderAccess.js`, thay vì tự chép lại role set. `null` = chưa tải
  // xong (mới load trang / đang refresh token) -> orderAccess.js tự rơi về
  // fallback role-based để ô nhập không nháy sáng-rồi-khoá.
  fieldPermissions: null,
  loading: false,
  error: null
};

export const mutations = {
  SET_TOKEN(state, token) {
    state.accessToken = token;
  },

  SET_CURRENT_USER(state, user) {
    state.currentUser = user;
  },

  SET_FIELD_PERMISSIONS(state, canEdit) {
    state.fieldPermissions = canEdit;
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  CLEAR_ERROR(state) {
    state.error = null;
  },

  CLEAR_AUTH(state) {
    state.accessToken = null;
    state.currentUser = null;
    state.fieldPermissions = null;
  }
};

export const getters = {
  /**
   * Whether the user is currently logged in
   */
  loggedIn(state) {
    return !!state.accessToken && !!state.currentUser;
  },

  /**
   * Get current user
   */
  currentUser(state) {
    return state.currentUser;
  },

  /**
   * Ma trận quyền field tiền của user hiện tại
   * ({price, order_item_price, payment, cod, freeship}),
   * hoặc `null` nếu chưa tải xong.
   */
  fieldPermissions(state) {
    return state.fieldPermissions;
  },

  /**
   * Get access token
   */
  accessToken(state) {
    return state.accessToken;
  },

  /**
   * Check if user has specific role
   */
  hasRole: (state) => (roleName) => {
    return state.currentUser?.role_name === roleName;
  }
};

export const actions = {
  /**
   * Initialize auth state from stored token
   */
  init({ commit, dispatch }) {
    const token = tokenManager.getAccessToken();
    if (token) {
      commit('SET_TOKEN', token);
      // Try to fetch user data
      dispatch('fetchCurrentUser').catch(() => {
        // Token invalid, clear auth
        dispatch('logout');
      });
    }
  },

  /**
   * Bước 1: mật khẩu → { mfa_token, setup_required }. Chưa có token thật.
   */
  async login({ commit }, { username, password }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      return await authApi.login({ username, password });
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.detail || 'Login failed');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Bước 2: mã 2FA → lưu token thật + tải user.
   */
  async verifyTwoFactor({ commit, dispatch }, { mfaToken, code }) {
    const response = await authApi.twoFactorVerify(mfaToken, code);
    tokenManager.setTokens(response.access_token, response.refresh_token);
    commit('SET_TOKEN', response.access_token);
    await dispatch('fetchCurrentUser');
    return response;
  },

  /**
   * Logout user and clear tokens
   */
  async logout({ commit }) {
    try {
      // Call logout endpoint (optional, as tokens are invalidated on server)
      await authApi.getMe();
    } catch (error) {
      // Ignore error during logout
    } finally {
      tokenManager.clearTokens();
      commit('CLEAR_AUTH');
    }
  },

  /**
   * Fetch current user data
   */
  async fetchCurrentUser({ commit, state }) {
    if (!state.accessToken) {
      return Promise.resolve(null);
    }

    commit('SET_LOADING', true);

    try {
      const user = await authApi.getMe();
      commit('SET_CURRENT_USER', user);

      // Quyền field tiền tải RIÊNG, không chặn đăng nhập nếu lỗi (VD API mới
      // triển khai lệch giữa BE/FE) — orderAccess.js tự rơi về fallback role-based
      // khi store chưa có dữ liệu này.
      try {
        const permissions = await authApi.getFieldPermissions();
        commit('SET_FIELD_PERMISSIONS', permissions?.can_edit || null);
      } catch (permError) {
        commit('SET_FIELD_PERMISSIONS', null);
      }

      return user;
    } catch (error) {
      // Token may be invalid
      tokenManager.clearTokens();
      commit('CLEAR_AUTH');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Change password
   */
  async changePassword({ commit }, { oldPassword, newPassword }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      await authApi.changePassword({
        old_password: oldPassword,
        new_password: newPassword
      });

      // Optionally re-login or update token
      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Password change failed';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Update current user profile (full_name, email, phone)
   */
  async updateProfile({ commit }, profileData) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const user = await authApi.updateMe(profileData);
      commit('SET_CURRENT_USER', user);
      return user;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Update failed';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Clear error state
   */
  clearError({ commit }) {
    commit('CLEAR_ERROR');
  }
};
